import React, { useState, useEffect } from "react";
import { useSupabase } from "../SupabaseContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2, AlertCircle, Building2 } from "lucide-react";
import { Separator } from "./ui/separator";
import { useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";

const INITIAL_FORM_STATE = {
  firstName: "",
  lastName: "",
  position: "",
  email: "",
  id_code: "",
};

const AdminRegistrationForm = ({
  initialData = null,
  onSuccess,
  onClose,
  facilities = [],
  categories = [],
  supabase: providedSupabase,
}) => {
  const supabaseContext = useSupabase();
  const supabase = providedSupabase || supabaseContext;
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [selectedFacilities, setSelectedFacilities] = useState([]); // Changed to array for multiple facilities
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const [selectedCategories, setSelectedCategories] = useState({});

  useEffect(() => {
    const initializeForm = async () => {
      try {
        setIsLoading(true);

        if (initialData) {
          setFormData({
            firstName: initialData.first_name || "",
            lastName: initialData.last_name || "",
            position: initialData.position || "",
            email: initialData.email || "",
            id_code: initialData.id_code || "",
          });

          // Fetch user's facilities from the junction table if editing an existing user
          if (initialData.id) {
            await fetchUserFacilities(initialData.id);
            await fetchUserCategories(initialData.id);
          }
        }
      } catch (error) {
        console.error("Error initializing form:", error);
        setError("Failed to initialize form data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [initialData]);

  const fetchUserFacilities = async (userId) => {
    try {
      console.log("Fetching facilities for user ID:", userId);

      // If the user had a direct facility_id in the users table (legacy data)
      if (initialData && initialData.facility_id) {
        console.log("User has legacy facility_id:", initialData.facility_id);
        setSelectedFacilities([initialData.facility_id]);
      }

      // Get facilities from the junction table
      const { data, error } = await supabase
        .from("user_facilities")
        .select("facility_id")
        .eq("user_id", userId)
        .eq("is_active", true);

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        console.log("Found facilities in junction table:", data);
        setSelectedFacilities(data.map((item) => item.facility_id));
      }
    } catch (error) {
      console.error("Error fetching user facilities:", error);
      throw new Error("Failed to load user facilities");
    }
  };

  const fetchUserCategories = async (userId) => {
    try {
      const { data, error } = await supabase
        .from("user_category_values")
        .select("category_id, option_id")
        .eq("user_id", userId);

      if (error) throw error;

      const categoryValues = {};
      data.forEach((item) => {
        categoryValues[item.category_id] = item.option_id;
      });
      setSelectedCategories(categoryValues);
    } catch (error) {
      console.error("Error fetching user categories:", error);
      throw new Error("Failed to load user categories");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) return "First name is required";
    if (!formData.lastName.trim()) return "Last name is required";
    if (selectedFacilities.length === 0)
      return "At least one facility selection is required";
    if (!formData.email.trim()) return "Email is required";
    if (!formData.email.includes("@")) return "Invalid email format";
    if (!formData.id_code.trim()) return "Password is required";
    return null;
  };

  // Funkcia na aktualizáciu hesla cez API endpoint
  const updatePasswordViaAPI = async (userId, newPassword) => {
    try {
      const response = await fetch("/api/update-user-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          newPassword: newPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to update password");
      }

      return result;
    } catch (error) {
      console.error("API call error:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      if (initialData?.id) {
        // Update existing employee
        const employeeData = {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          position: formData.position,
          id_code: formData.id_code,
          company_id: user.company_id,
          is_active: true,
        };

        // Kontrola či sa heslo zmenilo
        const passwordChanged = formData.id_code !== initialData.id_code;

        if (passwordChanged) {
          // Ak sa heslo zmenilo, použijeme API endpoint
          console.log("Password changed, updating via API...");
          await updatePasswordViaAPI(initialData.id, formData.id_code);

          // Aktualizujeme ostatné údaje bez hesla
          const { id_code, ...otherData } = employeeData;
          const { error: updateError } = await supabase
            .from("users")
            .update(otherData)
            .eq("id", initialData.id);

          if (updateError) throw updateError;
        } else {
          // Ak sa heslo nezmenilo, štandardný update
          const { error: updateError } = await supabase
            .from("users")
            .update(employeeData)
            .eq("id", initialData.id);

          if (updateError) throw updateError;
        }

        // Handle facility assignments using the junction table
        const { error: deleteError } = await supabase
          .from("user_facilities")
          .delete()
          .eq("user_id", initialData.id);

        if (deleteError) {
          console.error("Error deleting existing facilities:", deleteError);
        }

        if (selectedFacilities.length > 0) {
          const facilityEntries = selectedFacilities.map((facilityId) => ({
            user_id: initialData.id,
            facility_id: facilityId,
            is_active: true,
          }));

          const { error: facilityError } = await supabase
            .from("user_facilities")
            .insert(facilityEntries);

          if (facilityError) throw facilityError;
        }

        // Handle category assignments
        const { error: deleteCatError } = await supabase
          .from("user_category_values")
          .delete()
          .eq("user_id", initialData.id);

        if (deleteCatError) {
          console.error("Error deleting existing categories:", deleteCatError);
        }

        const categoryValues = Object.entries(selectedCategories)
          .filter(([_, value]) => value)
          .map(([categoryId, optionId]) => ({
            user_id: initialData.id,
            category_id: categoryId,
            option_id: optionId,
          }));

        if (categoryValues.length > 0) {
          const { error: categoryError } = await supabase
            .from("user_category_values")
            .insert(categoryValues);

          if (categoryError) throw categoryError;
        }
      } else {
        // Create new employee via API (creates auth user + public.users + facilities + categories)
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;
        if (!session?.access_token) {
          throw new Error(
            "Nie ste prihlásený. Skúste sa odhlásiť a prihlásiť znova."
          );
        }

        const categoryValues = Object.entries(selectedCategories)
          .filter(([_, value]) => value)
          .map(([categoryId, optionId]) => ({
            category_id: categoryId,
            option_id: optionId,
          }));

        const response = await fetch("/api/create-employee", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            companyId: user.company_id,
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            password: formData.id_code,
            position: formData.position,
            selectedFacilities,
            categoryValues,
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Nepodarilo sa vytvoriť zamestnanca");
        }
      }

      // Reset form state
      setFormData(INITIAL_FORM_STATE);
      setSelectedFacilities([]);
      setSelectedCategories({});

      // Call the success callback if provided
      if (onSuccess) {
        console.log("Calling success callback");
        onSuccess();
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      setError(error.message || "An error occurred while saving");
    } finally {
      setIsLoading(false);
    }
  };

  const renderCategories = () => (
    <div className="space-y-4">
      <Label className="font-medium">Categories</Label>
      {categories.map((category) => (
        <div key={category.id} className="space-y-2">
          <Label>{category.name}</Label>
          <Select
            value={selectedCategories[category.id] || ""}
            onValueChange={(value) =>
              setSelectedCategories((prev) => ({
                ...prev,
                [category.id]: value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {category.custom_category_options.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[60vh] pr-4 overflow-y-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Meno"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <FormField
            label="Priezvisko"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="space-y-2">
          <Label>Pobočky / Skupiny</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedFacilities.length > 0 &&
              facilities.length > 0 &&
              selectedFacilities.map((facilityId) => {
                const facility = facilities.find((f) => f.id === facilityId);
                return facility ? (
                  <Badge
                    key={facilityId}
                    variant="outline"
                    className="bg-blue-50"
                  >
                    <Building2 className="mr-1 w-3 h-3" />
                    {facility.name}
                  </Badge>
                ) : null;
              })}
            {selectedFacilities.length === 0 && (
              <span className="text-sm text-gray-400">
                Nie sú vybrané žiadne pobočky
              </span>
            )}
          </div>
          <ScrollArea className="h-[200px] border rounded-md p-4">
            {facilities.length === 0 ? (
              <div className="p-2 text-gray-500">Žiadne dostupné pobočky</div>
            ) : (
              facilities.map((facility) => (
                <div
                  key={facility.id}
                  className="flex items-center py-2 space-x-2"
                >
                  <Checkbox
                    id={`facility-${facility.id}`}
                    checked={selectedFacilities.includes(facility.id)}
                    onCheckedChange={(checked) => {
                      setSelectedFacilities((prev) =>
                        checked
                          ? [...prev, facility.id]
                          : prev.filter((id) => id !== facility.id)
                      );
                    }}
                  />
                  <Label
                    htmlFor={`facility-${facility.id}`}
                    className="flex items-center cursor-pointer"
                  >
                    <Building2 className="mr-2 w-4 h-4 text-gray-500" />
                    {facility.name}
                  </Label>
                </div>
              ))
            )}
          </ScrollArea>
        </div>

        <FormField
          label="Pozícia"
          name="position"
          value={formData.position}
          onChange={handleChange}
        />
        <FormField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
        <FormField
          label="Heslo"
          name="id_code"
          value={formData.id_code}
          onChange={handleChange}
          required
        />

        {categories.length > 0 && (
          <>
            <Separator className="my-4" />
            {renderCategories()}
          </>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col space-y-2">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                {initialData ? "Upravujem..." : "Vytváram..."}
              </>
            ) : initialData ? (
              "Upraviť zamestnanca"
            ) : (
              "Vytvoriť zamestnanca"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

const FormField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
}) => (
  <div className="space-y-2">
    <Label htmlFor={name}>{label}</Label>
    <Input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
    />
  </div>
);

export default AdminRegistrationForm;
